import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import './Login.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4310';

const Login = ({ onLoginSuccess, onError, onSuccess }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [authProgress, setAuthProgress] = useState(0);
  const [signupBonusCredits, setSignupBonusCredits] = useState(30);
  const authTriggeredRef = useRef(false);

  // Fetch dynamic system config (signup credits bonus)
  useEffect(() => {
    fetch(`${API_URL}/api/downloads`)
      .then(res => res.json())
      .then(data => {
        if (data.success && typeof data.signupCredits === 'number') {
          setSignupBonusCredits(data.signupCredits);
        }
      })
      .catch(err => console.log('Using default signup credits:', err));
  }, []);

  // Initiate Google OAuth Redirect
  const handleGoogleLogin = async () => {
    try {
      const redirectUri = window.location.origin + '/login';
      const response = await fetch(`${API_URL}/api/auth/google-oauth-url?redirectUri=${encodeURIComponent(redirectUri)}`);
      const data = await response.json();

      if (data.success && data.authUrl) {
        window.location.href = data.authUrl;
      } else {
        if (onError) onError('Failed to retrieve authentication URL: ' + (data.message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error fetching Google OAuth URL:', err);
      if (onError) onError(`Connection failed. Please ensure the backend server is running at ${API_URL}`);
    }
  };

  // Listen for Google Redirect Code Callback in URL query params on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');

    if (code && !authTriggeredRef.current) {
      authTriggeredRef.current = true;
      // Strip ?code= query param immediately so it cannot be re-triggered on refresh/logout
      window.history.replaceState({}, document.title, window.location.pathname);
      setIsAuthenticating(true);
      setAuthProgress(15);

      const interval = setInterval(() => {
        setAuthProgress(prev => (prev >= 90 ? 90 : prev + 12));
      }, 140);

      const exchangeOAuthCode = async () => {
        try {
          const redirectUri = window.location.origin + '/login';
          const response = await fetch(`${API_URL}/api/auth/google-exchange`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ code, redirectUri })
          });

          const data = await response.json();
          clearInterval(interval);

          if (data.success && data.token) {
            setAuthProgress(100);

            localStorage.setItem('token', data.token);

            // Sync with Electron desktop app if open
            try {
              const iframe = document.createElement('iframe');
              iframe.src = `crackit://auth?authToken=${encodeURIComponent(data.token)}`;
              iframe.style.display = 'none';
              document.body.appendChild(iframe);
              setTimeout(() => {
                if (document.body.contains(iframe)) {
                  document.body.removeChild(iframe);
                }
              }, 1000);
            } catch (err) {
              console.error('Failed to trigger desktop auth sync:', err);
            }

            // Direct local HTTP sync backup
            try {
              fetch('http://127.0.0.1:4312', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                mode: 'no-cors',
                body: JSON.stringify({ authToken: data.token })
              }).catch(() => console.log('Local sync server offline'));
            } catch (err) { }

            setTimeout(() => {
              setIsAuthenticating(false);
              onLoginSuccess(data.user);
              const from = location.state?.from?.pathname || (data.user?.role === 'admin' ? '/admin' : '/dashboard');
              navigate(from, { replace: true });
            }, 700);
          } else {
            if (onError) onError('Google Authentication failed: ' + (data.message || 'Unauthorized'));
            setIsAuthenticating(false);
            navigate('/login', { replace: true });
          }
        } catch (error) {
          console.error('Error exchanging code:', error);
          clearInterval(interval);
          if (onError) onError(`Failed to communicate with login server. Ensure ${API_URL} is active.`);
          setIsAuthenticating(false);
          navigate('/login', { replace: true });
        }
      };

      exchangeOAuthCode();
    }
  }, [navigate, onLoginSuccess, onError, location.state]);

  return (
    <div className="space-login-page">
      {/* Subtle Space Background Ambient */}
      <div className="space-login-radial"></div>
      <div className="space-circuit-grid"></div>

      {/* Center Glass Card */}
      <div className="space-login-container">
        <motion.div
          className="space-login-card glass-card"
          initial={{ opacity: 0, y: 24, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        >
          {/* Brand Logo Clickable Header */}
          <div className="login-brand-header" onClick={() => navigate('/')} title="Return to Home">
            <div className="login-logo-bolt">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
              </svg>
            </div>
            <span className="login-logo-text">CrackIT <span className="login-logo-badge">AI</span></span>
          </div>

          <AnimatePresence mode="wait">
            {!isAuthenticating ? (
              <motion.div
                key="google-login-panel"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="login-panel-content"
              >
                {/* Badge Pill */}
                <div className="badge-pill">
                  <span className="eyebrow-dot"></span>
                  <span>SECURE SIGN IN</span>
                </div>

                {/* Main Heading & Subtitle */}
                <h1 className="login-main-title">
                  Access <span className="gradient-primary">Copilot</span>
                </h1>

                <p className="login-main-subtitle">
                  Sign in with Google to access your real-time interview copilot, resumes, and credits.
                </p>

                {/* Bonus Credits Notice */}
                <div className="login-bonus-banner">
                  <div className="bonus-icon-wrapper">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                    </svg>
                  </div>
                  <span className="bonus-text">
                    Get <strong>{signupBonusCredits} Free Credits</strong> on your first sign-in
                  </span>
                </div>

                {/* Primary Google Auth Action Button */}
                <button
                  type="button"
                  className="space-google-btn"
                  onClick={handleGoogleLogin}
                >
                  <div className="google-icon-wrapper">
                    <svg className="google-official-svg" viewBox="0 0 24 24" width="18" height="18">
                      <path
                        fill="#4285F4"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="#34A853"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
                      />
                      <path
                        fill="#EA4335"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
                      />
                    </svg>
                  </div>
                  <span className="google-btn-text">Continue with Google</span>
                  <svg className="btn-chevron-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14"></path>
                    <path d="m12 5 7 7-7 7"></path>
                  </svg>
                </button>

                {/* Feature Checklist */}
                <div className="login-features-box">
                  <div className="login-feature-item">
                    <div className="check-bullet-icon">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <span>100% Invisible hardware overlay</span>
                  </div>
                  <div className="login-feature-item">
                    <div className="check-bullet-icon">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <span>Real-time voice &amp; screen transcription</span>
                  </div>
                  <div className="login-feature-item">
                    <div className="check-bullet-icon">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <span>Works with Zoom, Meet, Teams &amp; LeetCode</span>
                  </div>
                </div>

                {/* Social Proof Row */}
                <div className="login-social-proof">
                  <div className="login-avatars">
                    <img src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=60&auto=format&fit=crop&q=80" alt="Candidate" />
                    <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=60&auto=format&fit=crop&q=80" alt="Candidate" />
                    <img src="https://images.unsplash.com/photo-1517841905240-472988babdf9?w=60&auto=format&fit=crop&q=80" alt="Candidate" />
                    <img src="https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=60&auto=format&fit=crop&q=80" alt="Candidate" />
                  </div>
                  <span className="proof-caption">Joined by <strong>10,000+</strong> candidates</span>
                </div>

                {/* Security & Legal Footer */}
                <div className="login-legal-footer">
                  <p>
                    Official Google OAuth 2.0. By continuing, you agree to our{' '}
                    <Link to="/terms">Terms</Link>,{' '}
                    <Link to="/privacy">Privacy</Link>, &amp;{' '}
                    <Link to="/refund-policy">Refund Policy</Link>.
                  </p>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="auth-progress-panel"
                className="space-auth-progress"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
              >
                <div className="space-spinner-halo">
                  <div className="space-spinner-core">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                    </svg>
                  </div>
                </div>
                <h3 className="auth-progress-title">AUTHENTICATING</h3>
                <p className="auth-progress-desc">
                  Establishing OAuth session... {authProgress}%
                </p>

                <div className="auth-progress-track">
                  <div className="auth-progress-bar" style={{ width: `${authProgress}%` }}></div>
                </div>

                <span className="auth-sub-note">Redirecting to dashboard...</span>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
};

export default Login;
